import { useTranslation } from 'react-i18next';
import { RecentSearch, clearRecentSearches } from '@/lib/recent-searches';
import { SearchResult } from '@/lib/search-utils';
import { User, Briefcase, FileText } from 'lucide-react';

export default function RecentSearchesPanel({
  searches,
  onSelectSearch,
  selectedResult,
}: {
  searches: RecentSearch[];
  onSelectSearch: (search: RecentSearch) => void;
  selectedResult?: SearchResult | null;
}) {
  const { t } = useTranslation();

  const getIcon = (type: 'contact' | 'deal' | 'note') => {
    switch (type) {
      case 'contact':
        return <User className="w-5 h-5 text-muted-foreground flex-shrink-0" />;
      case 'deal':
        return (
          <Briefcase className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        );
      case 'note':
        return <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />;
    }
  };

  const isSelected = (search: RecentSearch) => {
    return selectedResult?.id === search.id && selectedResult?.type === search.type;
  };

  const grouped = {
    deals: searches.filter((s) => s.type === 'deal'),
    contacts: searches.filter((s) => s.type === 'contact'),
    notes: searches.filter((s) => s.type === 'note'),
  };

  if (searches.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-muted-foreground">{t("search.noPreviousSearches")}</p>
      </div>
    );
  }

  const handleClear = () => {
    clearRecentSearches();
    window.dispatchEvent(new Event('recentSearchesCleared'));
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header – Bigin style: title left, Clear right */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{t("search.previousSearches")}</h3>
        <button
          type="button"
          onClick={handleClear}
          className="text-sm font-medium text-primary hover:underline shrink-0"
        >
          {t("search.clearRecent")}
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {/* Deals Section */}
        {grouped.deals.length > 0 && (
          <div>
            <div className="px-4 py-3 bg-muted sticky top-0 z-10">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                {t("search.dealsSection")} ({grouped.deals.length})
              </h3>
            </div>
            <div className="divide-y divide-border">
              {grouped.deals.map((search) => (
                <button
                  key={`${search.type}-${search.id}`}
                  onClick={() => onSelectSearch(search)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    isSelected(search)
                      ? 'bg-muted'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  {getIcon(search.type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {search.title}
                    </p>
                    {search.subtitle && (
                      <p className="text-xs text-muted-foreground truncate">
                        {search.subtitle}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Contacts Section */}
        {grouped.contacts.length > 0 && (
          <div>
            <div className="px-4 py-3 bg-muted sticky top-0 z-10">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                {t("search.contactsSection")} ({grouped.contacts.length})
              </h3>
            </div>
            <div className="divide-y divide-border">
              {grouped.contacts.map((search) => (
                <button
                  key={`${search.type}-${search.id}`}
                  onClick={() => onSelectSearch(search)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    isSelected(search)
                      ? 'bg-muted'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  {getIcon(search.type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {search.title}
                    </p>
                    {search.subtitle && (
                      <p className="text-xs text-muted-foreground truncate">
                        {search.subtitle}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Notes Section */}
        {grouped.notes.length > 0 && (
          <div>
            <div className="px-4 py-3 bg-muted sticky top-0 z-10">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                Jegyzetek ({grouped.notes.length})
              </h3>
            </div>
            <div className="divide-y divide-border">
              {grouped.notes.map((search) => (
                <button
                  key={`${search.type}-${search.id}`}
                  onClick={() => onSelectSearch(search)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    isSelected(search)
                      ? 'bg-muted'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  {getIcon(search.type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {search.title}
                    </p>
                    {search.subtitle && (
                      <p className="text-xs text-muted-foreground truncate">
                        {search.subtitle}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
